import { Slide, Table, TableCell, TextProps } from '@features/powerpoint';
import { contentSlideHeader, generateTable } from '../../../../utils';
import { CampaignAnalyticsReport } from '@components/Dashboard/CampaignAnalytics/slice/schema';
import { sortBy } from '@utils/arrays';
import { TABLE_HEADER_OPTIONS } from '../sales-attribution/rsa-dimensions-table-slide';
import { postalCodeToStateName } from '@utils/states';
import { CampaignAnalyticsSlideProps } from './build-campaign-analytics-slides';

const ROWS_PER_TABLE = 20;
const NUMBER_WIDTH = 1;
const STATE_WIDTH = 4;
const VISITS_DISCLAIMER = 'Visit Index (Average = 100)';

const stateVisitColumnLabels: TableCell[] = [
  {
    text: 'Rank',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'State',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Visit Index',
    options: { ...TABLE_HEADER_OPTIONS, align: 'right' },
  },
];

const dmaVisitColumnLabels: TableCell[] = [
  {
    text: 'Rank',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'DMA',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Visit Index',
    options: { ...TABLE_HEADER_OPTIONS, align: 'right' },
  },
];

const stateVisitRows = (
  stateVisits: CampaignAnalyticsReport['stateVisits']
): TableCell[][] => {
  const topStatesRows = stateVisits.data
    .toSorted(sortBy('visitIndex', 'desc'))
    .slice(0, ROWS_PER_TABLE)
    .map((stateVisit, index): TableCell[] => {
      const rank = (index + 1).toString();

      return [
        { text: rank, options: { align: 'left' } },
        {
          text:
            postalCodeToStateName[
              stateVisit.location as keyof typeof postalCodeToStateName
            ] ?? stateVisit.location,
          options: { align: 'left' },
        },
        { text: stateVisit.visitIndex.toString(), options: { align: 'right' } },
      ];
    });

  return topStatesRows;
};

const dmaVisitRows = (
  dmaVisits: CampaignAnalyticsReport['dmaVisits']
): TableCell[][] => {
  const topStatesRows = dmaVisits.data
    .toSorted(sortBy('visitIndex', 'desc'))
    .slice(0, ROWS_PER_TABLE)
    .map((dmaVisit, index): TableCell[] => {
      const rank = (index + 1).toString();

      return [
        { text: rank, options: { align: 'left' } },
        { text: dmaVisit.location, options: { align: 'left' } },
        { text: dmaVisit.visitIndex.toString(), options: { align: 'right' } },
      ];
    });

  return topStatesRows;
};

export const stateAndDmaVisitsTableSlide = (
  props: CampaignAnalyticsSlideProps
): Slide => {
  const tables: Table[] = [
    ...(props.campaignAnalyticsReport.stateVisits.visible
      ? [
          generateTable(
            stateVisitColumnLabels,
            stateVisitRows(props.campaignAnalyticsReport.stateVisits),
            {
              rowH: 0.1,
              w: 6.13,
              colW: [NUMBER_WIDTH, STATE_WIDTH, NUMBER_WIDTH],
              y: 1.78,
              x: 0.44,
            }
          ),
        ]
      : []),
    ...(props.campaignAnalyticsReport.dmaVisits.visible
      ? [
          generateTable(
            dmaVisitColumnLabels,
            dmaVisitRows(props.campaignAnalyticsReport.dmaVisits),
            {
              rowH: 0.1,
              w: 6.13,
              x: 6.87,
              colW: [NUMBER_WIDTH, STATE_WIDTH, NUMBER_WIDTH],
              y: 1.78,
            }
          ),
        ]
      : []),
  ];

  const texts: TextProps[] = [
    contentSlideHeader('Top State & DMA Visits', { x: 0.75, y: 0.6 }),
    { text: VISITS_DISCLAIMER, options: { x: 0.77, y: 6.88, fontSize: 8 } },
    ...(props.campaignAnalyticsReport.stateVisits.visible
      ? [
          {
            text: 'Top 20 States',
            options: {
              x: 0.44,
              y: 1.41,
              h: 0.47,
              w: 3.26,
              fontSize: 10,
              bold: true,
            },
          },
        ]
      : []),
    ...(props.campaignAnalyticsReport.dmaVisits.visible
      ? [
          {
            text: 'Top 20 DMAs',
            options: {
              x: 6.87,
              y: 1.41,
              h: 0.47,
              w: 3.26,
              fontSize: 10,
              bold: true,
            },
          },
        ]
      : []),
  ];

  return {
    slideMaster: props.slideMaster,
    tables,
    texts,
  };
};
